import React, { useState } from "react";
import { Card, CardBody, CardHeader, Switch, Listbox, ListboxItem } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { useTheme } from "@/hooks/use-theme";
import { type AvailableLanguage } from "@/i18n";
import { SunFilledIcon, MoonFilledIcon } from "@/components/icons";

const AVAILABLE_LANGUAGES: AvailableLanguage[] = [
  { code: "en-GB", nativeName: "English", isRTL: false, isDefault: true },
  { code: "de-DE", nativeName: "Deutsch", isRTL: false },
  { code: "es-ES", nativeName: "Español", isRTL: false },
  { code: "fr-FR", nativeName: "Français", isRTL: false },
  { code: "it-IT", nativeName: "Italiano", isRTL: false },
  { code: "pt-PT", nativeName: "Português", isRTL: false },
];

export const AppearanceSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const handleLanguageChange = (keys: "all" | Set<React.Key>) => {
    const selectedKey = Array.from(keys)[0] as string;

    if (selectedKey) {
      setSelectedLanguage(selectedKey);
      i18n.changeLanguage(selectedKey);
      localStorage.setItem("preferredLanguage", selectedKey);
      document.documentElement.lang = selectedKey;
    }
  };

  const getLanguageShortCode = (code: string): string => {
    return code === "en-GB" ? "EN" : code.split("-")[1];
  };

  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <span>{t("Appearance")}</span>
        </h2>
      </CardHeader>
      <CardBody className="space-y-6">
        {/* Theme Toggle */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-medium">{t("Dark Mode")}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("Toggle dark mode theme")}
            </p>
          </div>
          <Switch
            aria-label={isDark ? t("Switch to light mode") : t("Switch to dark mode")}
            data-testid="theme-switch"
            endContent={<SunFilledIcon size={18} />}
            isSelected={isDark}
            size="lg"
            startContent={<MoonFilledIcon size={18} />}
            onValueChange={toggleTheme}
          />
        </div>

        {/* Language Selector */}
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="font-medium">{t("Language")}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("Select your preferred language")}
            </p>
          </div>
          <Listbox
            aria-label={t("Select language")}
            classNames={{
              base: "border border-default-200 rounded-lg p-1",
            }}
            data-testid="language-listbox"
            selectedKeys={new Set([selectedLanguage])}
            selectionMode="single"
            onSelectionChange={handleLanguageChange}
          >
            {AVAILABLE_LANGUAGES.map((lang) => (
              <ListboxItem
                key={lang.code}
                classNames={{
                  base: "data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary",
                }}
                textValue={lang.nativeName}
              >
                <div className="flex items-center justify-between w-full">
                  <span>{lang.nativeName}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {getLanguageShortCode(lang.code)}
                  </span>
                </div>
              </ListboxItem>
            ))}
          </Listbox>
        </div>
      </CardBody>
    </Card>
  );
};
